import mixpanel from 'mixpanel-browser';

class MixpanelService {
  private isInitialized = false;
  private readonly projectToken: string;
  private readonly includeSpoorUsers: boolean;

  constructor() {
    this.projectToken = process.env.REACT_APP_MIXPANEL_TOKEN || '';
    this.includeSpoorUsers = process.env.REACT_APP_MIXPANEL_INCLUDE_SPOOR_USERS === 'true';
    if (!this.projectToken) {
      return;
    }
    this.initialize();
  }

  private shouldTrackUser(userEmail?: string): boolean {
    if (!userEmail) {
      return true;
    }
    
    if (this.includeSpoorUsers) {
      return true;
    }
    
    return !userEmail.endsWith('@spoor.ai');
  }

  private initialize(): void {
    try {
      mixpanel.init(this.projectToken, {
        debug: false,
        track_pageview: true,
        persistence: 'localStorage',
        api_host: 'https://api-eu.mixpanel.com'
      });
      this.isInitialized = true;
    } catch {
      // Silently fail
    }
  }

  track(event: string, properties?: Record<string, any>, userEmail?: string): void {
    if (!this.isInitialized || !this.shouldTrackUser(userEmail)) {
      return;
    }

    try {
      mixpanel.track(event, {
        ...properties,
        timestamp: new Date().toISOString(),
        environment: process.env.NODE_ENV
      });
    } catch {
      // Silently fail
    }
  }

  identify(userId: string, userProperties?: Record<string, any>, userEmail?: string): void {
    if (!this.isInitialized || !this.shouldTrackUser(userEmail)) {
      return;
    }

    try {
      mixpanel.identify(userId);
      if (userProperties) {
        mixpanel.people.set(userProperties);
      }
    } catch {
      // Silently fail
    }
  }

  setUserProperties(properties: Record<string, any>, userEmail?: string): void {
    if (!this.isInitialized || !this.shouldTrackUser(userEmail)) {
      return;
    }

    try {
      mixpanel.people.set(properties);
    } catch {
      // Silently fail
    }
  }

  trackPageView(pageName: string, properties?: Record<string, any>, userEmail?: string): void {
    this.track('Page View', {
      page_name: pageName,
      ...properties
    }, userEmail);
  }

  trackUserAction(action: string, properties?: Record<string, any>, userEmail?: string): void {
    this.track('User Action', {
      action,
      ...properties
    }, userEmail);
  }

  trackError(error: Error, context?: Record<string, any>, userEmail?: string): void {
    this.track('Error', {
      error_message: error.message,
      error_stack: error.stack,
      error_name: error.name,
      ...context
    }, userEmail);
  }
}

export const mixpanelService = new MixpanelService();

export const trackEvent = (event: string, properties?: Record<string, any>, userEmail?: string) =>
  mixpanelService.track(event, properties, userEmail);

export const identifyUser = (userId: string, properties?: Record<string, any>, userEmail?: string) =>
  mixpanelService.identify(userId, properties, userEmail);

export const trackPageView = (pageName: string, properties?: Record<string, any>, userEmail?: string) =>
  mixpanelService.trackPageView(pageName, properties, userEmail);

export const trackUserAction = (action: string, properties?: Record<string, any>, userEmail?: string) =>
  mixpanelService.trackUserAction(action, properties, userEmail);

export const trackError = (error: Error, context?: Record<string, any>, userEmail?: string) =>
  mixpanelService.trackError(error, context, userEmail);
